"use client"

import { X, Heart, Crown, Briefcase } from "lucide-react"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import { ILeadershipRating } from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { dateFormat_YY_MM_DY_timeStamp } from "../../../shared/utils/utils"

interface RatingDetailsModalProps {
  selectedRow: ILeadershipRating | null
  getTemplateTitle: (templateId: string) => string
}

export const RatingDetailsModal = ({ selectedRow, getTemplateTitle }: RatingDetailsModalProps) => {
  const handleClose = () => {
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.RATING_DETAILS_MODAL)
  }

  const getDimensionIcon = (dimension: string) => {
    if (dimension === "Values") return <Heart className="h-4 w-4 text-blue-600" />
    if (dimension === "Leadership") return <Crown className="h-4 w-4 text-purple-600" />
    return <Briefcase className="h-4 w-4 text-green-600" />
  }

  if (!selectedRow) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {getDimensionIcon(selectedRow.dimension)}
            <h3 className="text-xl font-semibold text-slate-900">Rating Details</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Date</label>
              <p className="text-slate-900">{dateFormat_YY_MM_DY_timeStamp(selectedRow.date)}</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Dimension</label>
              <p className="text-slate-900">
                {selectedRow.dimension === "Values" || selectedRow.dimension === "Leadership"
                  ? selectedRow.dimension
                  : getTemplateTitle(selectedRow.dimension)}
              </p>
            </div>
          </div>

          {/* Comments Section */}
          {(selectedRow.userComment || selectedRow.adminComment) && (
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-800">Comments</h4>
              {selectedRow.userComment && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <label className="text-sm font-semibold text-blue-700">User Comment</label>
                  <p className="text-slate-900 mt-1">{selectedRow.userComment}</p>
                </div>
              )}
              {selectedRow.adminComment && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <label className="text-sm font-semibold text-purple-700">Admin Comment</label>
                  <p className="text-slate-900 mt-1">{selectedRow.adminComment}</p>
                </div>
              )}
            </div>
          )}

          {/* Feedback Details */}
          {Object.keys(selectedRow.criteria).length > 0 && (
            <div className="space-y-4">
              {Object.entries(selectedRow.criteria).map(([raterId, criteria]) => (
                <div key={raterId} className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-3">Feedback</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-semibold text-green-700">Start Doing</label>
                      <p className="text-slate-900">{criteria.startDoing || "No feedback provided"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-red-700">Stop Doing</label>
                      <p className="text-slate-900">{criteria.stopDoing || "No feedback provided"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-blue-700">Continue Doing</label>
                      <p className="text-slate-900">{criteria.continueDoing || "No feedback provided"}</p>
                    </div>
                    {criteria.additionalComment && (
                      <div>
                        <label className="text-sm font-semibold text-slate-700">Additional Comments</label>
                        <p className="text-slate-900">{criteria.additionalComment}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

